import { Component } from '@theme/component';
import { getStoreReviewSummary, formatReviewAverage } from '@theme/judgeme-review-data';

/**
 * Enhances the native Judge.me review widget without taking over its tabs.
 * Judge.me mounts asynchronously and can replace its markup in the theme editor.
 * @extends {Component}
 */
class JudgemeReviewTabs extends Component {
  /** @type {WeakSet<HTMLElement>} */
  #initializedWidgets = new WeakSet();

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {AbortController | undefined} */
  #formRequest;

  /** @type {AbortController | undefined} */
  #summaryRequest;

  /** @type {{ count: number, average: number } | undefined} */
  #storeSummary;

  #summaryKey = '';
  #summaryFailed = false;

  /** @type {Map<Text, { original: string, replacement: string }>} */
  #buttonLabels = new Map();

  /** @type {Map<HTMLElement, { node: HTMLElement, signature: string }>} */
  #summaries = new Map();

  /** @type {HTMLDivElement | undefined} */
  #storeReviewAction;

  #observer = new MutationObserver(() => this.#syncWidget());

  connectedCallback() {
    super.connectedCallback();
    this.#observeWidget();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeWidget();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#listeners?.abort();
    this.#formRequest?.abort();
    this.#summaryRequest?.abort();
    this.#summaryRequest = undefined;
    this.#restoreHeader();
    this.#section = null;
  }

  #observeWidget() {
    this.#observer.disconnect();
    this.#listeners?.abort();
    this.#formRequest?.abort();
    this.#summaryRequest?.abort();
    this.#summaryRequest = undefined;
    this.#summaryFailed = false;
    this.#restoreHeader();
    this.#section = this.closest('.shopify-section');
    if (!this.#section) return;

    this.#listeners = new AbortController();
    const options = { capture: true, signal: this.#listeners.signal };
    this.#section.addEventListener('click', this.#preserveUserChoice, options);
    this.#section.addEventListener('click', this.#handleReviewClick, options);
    this.#section.addEventListener('keydown', this.#preserveUserChoice, options);
    this.#section.addEventListener('change', this.#preserveUserChoice, options);
    this.#observer.observe(this.#section, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-selected'],
    });
    this.#syncWidget();
  }

  #syncWidget() {
    this.#selectDefaultTab();
    const widget = this.#section?.querySelector('.jdgm-review-widget[data-widget="review"]');
    if (!(widget instanceof HTMLElement) || widget.dataset.productId !== this.dataset.productId) return;
    const storeSelected = this.#isStoreSelected(widget);
    this.#syncStoreReviewAction(widget, storeSelected);
    this.#syncButtonLabels(widget, storeSelected);
    if (!storeSelected) {
      this.#summaryRequest?.abort();
      this.#summaryRequest = undefined;
      this.#summaryFailed = false;
      this.#restoreSummaries();
      return;
    }

    const count = widget.dataset.shopReviewsCount === undefined ? undefined : Number(widget.dataset.shopReviewsCount);
    const key = `judgeme-store-summary:${this.dataset.shopDomain}:${count}`;
    if (key !== this.#summaryKey) {
      this.#summaryRequest?.abort();
      this.#summaryRequest = undefined;
      this.#summaryKey = key;
      this.#storeSummary = undefined;
      this.#summaryFailed = false;
    }

    this.#syncSummaries(widget, this.#storeSummary?.count ?? count);
    if (!this.#storeSummary && !this.#summaryRequest && !this.#summaryFailed && this.dataset.shopDomain) {
      void this.#loadStoreSummary(count);
    }
  }

  #storeReviewLabel() {
    const nativeLabel = window.jdgmSettings?.widget_write_a_store_review_text;
    return nativeLabel && nativeLabel !== 'Write a Store Review' ? nativeLabel : this.dataset.writeStoreReview || nativeLabel;
  }

  /** @param {HTMLElement} widget @param {boolean} storeSelected */
  #syncStoreReviewAction(widget, storeSelected) {
    const nativeButton = widget.querySelector('[data-testid="write-review-button"]:not([data-judgeme-store-review-button])');
    const settings = window.jdgmSettings;
    const visibility = settings?.write_review_button_visibility || 'everyone';
    const canWrite = settings && !settings.disable_web_reviews && visibility !== 'no_one' &&
      (visibility !== 'signed_in_customers' || (widget.dataset.customerId && widget.dataset.customerEmail));
    const label = this.#storeReviewLabel();
    if (!storeSelected || nativeButton || !canWrite || !label) {
      if (this.#storeReviewAction?.contains(document.activeElement) && nativeButton instanceof HTMLButtonElement) {
        nativeButton.focus({ preventScroll: true });
      }
      this.#storeReviewAction?.remove();
      this.#storeReviewAction = undefined;
      return;
    }

    // Judge.me omits both its header action and empty-state action when a product
    // has zero reviews and the Store tab is active. Supply only that missing CTA.
    const target = widget.querySelector('.jm-review-widget-minimal-header__button-and-filters, .jm-review-widget-header__main-content, .jm-review-widget-sidebar-header__sidebar') ||
      widget.querySelector('.jm-review-widget-header__summary-section');
    if (!(target instanceof HTMLElement)) return;
    if (!this.#storeReviewAction || !target.contains(this.#storeReviewAction)) {
      this.#storeReviewAction?.remove();
      const action = document.createElement('div');
      action.className = 'judgeme-store-review-action';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'judgeme-store-review-button';
      button.dataset.testid = 'write-review-button';
      button.setAttribute('data-judgeme-store-review-button', '');
      button.textContent = label;
      action.append(button);
      target.append(action);
      this.#storeReviewAction = action;
    }
    const button = this.#storeReviewAction.querySelector('button');
    if (button && button.textContent !== label) button.textContent = label;
  }

  /** @param {HTMLElement} widget @param {boolean} storeSelected */
  #syncButtonLabels(widget, storeSelected) {
    for (const [node, label] of this.#buttonLabels) {
      if (!node.isConnected) this.#buttonLabels.delete(node);
      else if (!storeSelected) {
        if (node.data === label.replacement) node.data = label.original;
        this.#buttonLabels.delete(node);
      }
    }
    if (!storeSelected) return;
    const label = this.#storeReviewLabel();
    if (!label) return;
    for (const button of widget.querySelectorAll('[data-testid="write-review-button"]')) {
      const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!(node instanceof Text) || !node.data.trim()) continue;
        const saved = this.#buttonLabels.get(node);
        if (!saved || node.data !== saved.replacement) {
          this.#buttonLabels.set(node, { original: node.data, replacement: label });
        }
        if (node.data !== label) node.data = label;
        break;
      }
    }
  }

  /** @param {number | undefined} expectedCount */
  async #loadStoreSummary(expectedCount) {
    const request = new AbortController();
    this.#summaryRequest = request;
    try {
      const summary = await getStoreReviewSummary({
        shopDomain: this.dataset.shopDomain || '',
        expectedCount,
        signal: request.signal,
      });
      if (request.signal.aborted || this.#summaryRequest !== request) return;
      this.#storeSummary = summary;
    } catch {
      // Never substitute the product or combined rating when the store request fails.
      request.abort();
      if (this.#summaryRequest === request) this.#summaryFailed = true;
    } finally {
      if (this.#summaryRequest === request) {
        this.#summaryRequest = undefined;
        this.#syncWidget();
      }
    }
  }

  /** @param {HTMLElement} widget @param {number | undefined} count */
  #syncSummaries(widget, count) {
    const settings = window.jdgmSettings;
    const verifiedText = settings?.widget_show_verified_branding && settings.can_be_branded ? settings.verified_badge_text : '';
    const originals = [...widget.querySelectorAll('.jm-average-rating-display:not([data-judgeme-store-summary])')];
    const fallback = widget.querySelector('.jm-review-widget-header__title-and-rating, .jm-review-widget-minimal-header > .jm-cluster:first-child > .jm-stack:first-child, .jm-review-widget-sidebar-header__sidebar > .jm-stack:first-child');
    const targets = originals.length ? originals : fallback ? [fallback] : [];
    for (const [original, summary] of this.#summaries) {
      if (!targets.includes(original) || !original.isConnected) {
        original.removeAttribute('data-judgeme-product-summary');
        summary.node.remove();
        this.#summaries.delete(original);
      }
    }
    for (const original of targets) {
      if (!(original instanceof HTMLElement)) continue;
      const hasOriginal = originals.includes(original);
      if (hasOriginal) original.setAttribute('data-judgeme-product-summary', '');
      // The app retains ownership of its original DOM; only our sibling is replaced.
      const signature = `${hasOriginal ? original.outerHTML : ''}:${count}:${this.#storeSummary?.average}:${verifiedText}:${settings?.review_widget_review_count_enabled}`;
      const existing = this.#summaries.get(original);
      if (existing?.signature === signature && existing.node.isConnected) continue;
      existing?.node.remove();
      const node = hasOriginal ? /** @type {HTMLElement} */ (original.cloneNode(true)) : document.createElement('div');
      node.removeAttribute('data-judgeme-product-summary');
      node.setAttribute('data-judgeme-store-summary', '');
      node.removeAttribute('id');
      // A product review-site link does not describe this store summary.
      node.removeAttribute('href');
      node.removeAttribute('target');
      node.removeAttribute('aria-label');
      node.classList.remove('jm-average-rating-display--clickable');
      if (!hasOriginal) {
        node.className = 'judgeme-store-summary';
        node.innerHTML = '<strong class="jm-text judgeme-store-summary__rating"></strong><span class="jm-text judgeme-store-summary__count"></span>';
        if (verifiedText) {
          const badge = document.createElement('span');
          badge.className = 'judgeme-store-summary__verified';
          badge.innerHTML = '<svg class="judgeme-store-summary__verified-icon" viewBox="0 0 12 12" aria-hidden="true"><path fill="currentColor" d="M0.684155 0C0.306307 0 0 0.306307 0 0.684155V11.3159C0 11.6938 0.306307 12.0001 0.684155 12.0001H11.3158C11.6937 12.0001 12 11.6938 12 11.3159V6.8211C12 6.60481 11.7364 6.49889 11.5867 6.65505L9.21682 9.1281C8.20419 10.1848 7.14718 10.7102 6.0458 10.7044C4.95311 10.7072 3.91541 10.2173 2.93268 9.23457C1.88908 8.19097 1.33927 7.05861 1.28325 5.8375C1.23923 4.70995 1.67935 3.62286 2.60362 2.57623C2.7207 2.44364 2.92186 2.44188 3.04683 2.56685L5.12772 4.64774C5.25163 4.77165 5.25291 4.97316 5.14469 5.11144C4.64177 5.75402 4.62274 6.30774 5.0876 6.7726C5.55722 7.24222 6.08135 7.17511 6.66 6.57128L11.9333 1.06847C11.9761 1.02379 12 0.964298 12 0.902415V0.684155C12 0.306307 11.6937 0 11.3158 0H0.684155Z"/></svg>';
          const label = document.createElement('span');
          label.className = 'judgeme-store-summary__verified-text';
          label.textContent = verifiedText;
          badge.append(label);
          node.append(badge);
        }
      }
      const texts = [...node.querySelectorAll('.jm-text')].filter((text) => !text.closest('.jm-verified-badge'));
      const average = this.#storeSummary?.average;
      const formatted = average === undefined || !count ? '—' :
        formatReviewAverage(average);
      if (texts[0]) texts[0].textContent = formatted;
      if (texts[1]) {
        const word = count === 1 ? settings?.review_widget_review_word_singular : settings?.review_widget_review_word_plural;
        const text = count === undefined ? '' : `${count.toLocaleString()} ${word || this.dataset.reviewsLabel || ''}`.trim();
        texts[1].textContent = texts[1].textContent?.startsWith('(') ? `(${text})` : text;
        if (settings?.review_widget_review_count_enabled === false) texts[1].remove();
      }
      this.#updateSummaryStars(node, average);
      if (hasOriginal) original.after(node);
      else original.append(node);
      this.#summaries.set(original, { node, signature });
    }
  }

  /** @param {HTMLElement} node @param {number | undefined} average */
  #updateSummaryStars(node, average) {
    const stars = node.querySelector('.jm-star-rating');
    if (!stars) return;
    if (average === undefined) { stars.remove(); return; }
    // The adjacent numeric rating already supplies accessible text.
    stars.setAttribute('aria-hidden', 'true');
    stars.removeAttribute('aria-label');
    stars.removeAttribute('role');
    const rounded = Math.round(average * 2) / 2;
    const icons = stars.querySelectorAll('.jm-star-rating__font-icon, .jm-star-rating__filled, .jm-star-rating__half, .jm-star-rating__empty');
    icons.forEach((icon, index) => {
      const state = index + 1 <= rounded ? 'filled' : index < rounded ? 'half' : 'empty';
      if (icon.classList.contains('jm-star-rating__font-icon')) {
        icon.textContent = state === 'filled' ? '\uE000' : state === 'half' ? '\uE002' : '\uE001';
        return;
      }
      icon.classList.remove('jm-star-rating__filled', 'jm-star-rating__half', 'jm-star-rating__empty');
      icon.classList.add(`jm-star-rating__${state}`);
      const path = icon.querySelector('path');
      icon.querySelector('defs')?.remove();
      path?.removeAttribute('fill');
      if (state !== 'half' || !path) return;
      const svg = path.closest('svg');
      if (!svg) return;
      const id = `judgeme-store-star-${crypto.randomUUID()}`;
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.innerHTML = `<linearGradient id="${id}"><stop offset="50%" stop-color="var(--jm-review-widget-star-and-histogram-color)"/><stop offset="50%" stop-color="var(--jm-review-widget-star-and-histogram-color)" stop-opacity="0"/></linearGradient>`;
      svg.prepend(defs);
      path.setAttribute('fill', `url(#${id})`);
    });
  }

  #restoreSummaries() {
    for (const [original, summary] of this.#summaries) {
      original.removeAttribute('data-judgeme-product-summary');
      summary.node.remove();
    }
    this.#summaries.clear();
  }

  #restoreHeader() {
    this.#storeReviewAction?.remove();
    this.#storeReviewAction = undefined;
    this.#restoreSummaries();
    for (const [node, label] of this.#buttonLabels) {
      if (node.data === label.replacement) node.data = label.original;
    }
    this.#buttonLabels.clear();
  }

  /** @param {Event} event */
  #preserveUserChoice = (event) => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const widget = event.target.closest('.jdgm-review-widget[data-widget="review"]');
    if (widget instanceof HTMLElement) this.#initializedWidgets.add(widget);
  };

  /** @param {MouseEvent} event */
  #handleReviewClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-testid="write-review-button"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const widget = button.closest('.jdgm-review-widget[data-widget="review"]');
    if (!(widget instanceof HTMLElement) || widget.dataset.productId !== this.dataset.productId) return;

    // Leave the app's product-review handler in charge on the Product reviews tab.
    if (!this.#isStoreSelected(widget)) {
      this.#formRequest?.abort();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#formRequest) return;

    const url = this.#storeReviewUrl();
    const settings = window.jdgmSettings;
    if (settings?.modal_write_review_flow && settings.review_form_location === 'new_tab') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    void this.#openStoreReview(widget, button, url);
  };

  /** @param {HTMLElement} widget */
  #isStoreSelected(widget) {
    return widget.querySelector('#jdgm-tab_shop_reviews')?.getAttribute('aria-selected') === 'true';
  }

  #storeReviewUrl() {
    const jdgm = window.jdgm;
    const settings = window.jdgmSettings;
    const url = new URL(`${(jdgm?.API_HOST || 'https://api.judge.me').replace(/\/$/u, '')}/storefront_reviews/new`);
    url.searchParams.set('shop_domain', this.dataset.shopDomain || '');
    url.searchParams.set('platform', settings?.platform || 'shopify');
    url.searchParams.set('product_id', '0');
    if (settings?.shop_locale) url.searchParams.set('template_locale', settings.shop_locale);
    return url;
  }

  /**
   * @param {HTMLElement} widget
   * @param {HTMLButtonElement} button
   * @param {URL} fallbackUrl
   */
  async #openStoreReview(widget, button, fallbackUrl) {
    const request = new AbortController();
    this.#formRequest = request;
    const { signal } = request;
    const isCurrent = () => !signal.aborted && widget.isConnected && this.#isStoreSelected(widget);
    button.setAttribute('aria-busy', 'true');

    try {
      // The product widget already loads these dependencies; wait for slow networks.
      await this.#waitForReviewForm(signal);
      if (!isCurrent()) return;
      const jdgm = window.jdgm;
      let identity;
      jdgm.shopConsentToken = null;
      if (window.jdgmSettings?.sign_in_with_shop_enabled) {
        await jdgm.siwsReady;
        if (!isCurrent()) return;
        if (!jdgm.siws?.runChooser) throw new Error('Judge.me sign-in is unavailable');
        identity = await jdgm.siws.runChooser();
        if (identity?.dismissed || !isCurrent()) return;
        jdgm.shopConsentToken = identity?.shopConsentToken || null;
      }

      const customer = widget.dataset;
      jdgm.customerInfo = customer.customerId && customer.customerEmail ? {
        id: customer.customerId,
        email: customer.customerEmail,
        firstName: customer.customerFirstName,
        lastName: customer.customerLastName,
        fullName: customer.customerName,
      } : null;

      for (const existing of document.querySelectorAll('.jdgm-review-widget-modal.jdgm-write-review-modal')) {
        existing.remove();
      }
      const modal = new jdgm._WriteReviewModal(jdgm.$);
      // Match Judge.me's Happy Customers widget: 1 is its setup sentinel;
      // isLeavingShopReview makes the native submission use store ID 0.
      const ready = await modal.setup('jdgm-review-widget-modal', 1, {
        isLeavingShopReview: true,
        startedStoreReviewFromIntro: true,
        siwsSignedIn: Boolean(identity?.emailVerified),
      });
      if (!isCurrent()) {
        modal.$modal?.remove();
        return;
      }
      if (!ready) throw new Error('Judge.me store review form is unavailable');
      button.focus({ preventScroll: true });
      modal.showModalPage();
    } catch (error) {
      if (!isCurrent()) return;
      // Keep the correct review type even if the embedded app fails to load.
      console.warn('Unable to open Judge.me store review popup; opening its hosted form.', error);
      window.location.assign(fallbackUrl.href);
    } finally {
      button.removeAttribute('aria-busy');
      if (this.#formRequest === request) this.#formRequest = undefined;
    }
  }

  /** @param {AbortSignal} signal */
  #waitForReviewForm(signal) {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        clearInterval(interval);
        clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      };
      const abort = () => {
        cleanup();
        reject(new DOMException('Review form request cancelled', 'AbortError'));
      };
      const check = () => {
        const jdgm = window.jdgm;
        if (
          typeof jdgm?._WriteReviewModal !== 'function' ||
          typeof jdgm?.$ !== 'function' ||
          typeof jdgm?._renderAndSetupReviewForm !== 'function'
        ) return;
        cleanup();
        resolve();
      };
      const interval = setInterval(check, 100);
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Judge.me review form did not load'));
      }, 10000);
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      else check();
    });
  }

  #selectDefaultTab() {
    const widget = this.#section?.querySelector('.jdgm-review-widget[data-widget="review"]');
    if (!(widget instanceof HTMLElement) || this.#initializedWidgets.has(widget)) return;
    if (widget.dataset.productId !== this.dataset.productId || widget.dataset.shopReviews !== 'true') return;

    const productTab = widget.querySelector('#jdgm-tab_product_reviews');
    const storeTab = widget.querySelector('#jdgm-tab_shop_reviews');
    if (!(productTab instanceof HTMLButtonElement) || !(storeTab instanceof HTMLButtonElement)) return;
    if (productTab.disabled || storeTab.disabled) return;

    // Judge.me appends a localized count in parentheses to each native tab label.
    // Read the mounted product tab so editor previews and grouped reviews agree.
    const count = productTab.textContent?.trim().match(/\(([^()]*)\)$/u)?.[1];
    if (count === undefined) return;

    this.#initializedWidgets.add(widget);
    if (count !== (0).toLocaleString() || productTab.getAttribute('aria-selected') !== 'true') return;

    // The native click loads store reviews and updates the app's ARIA state.
    // Do this only once; subsequent tab choices remain entirely shopper-controlled.
    storeTab.click();
  }
}

if (!customElements.get('judgeme-review-tabs')) {
  customElements.define('judgeme-review-tabs', JudgemeReviewTabs);
}
